import React, { useEffect, useRef, useState } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project imports
import Transitions from 'ui-component/extended/Transitions';
import useConfig from 'hooks/useConfig';
import useAuth from 'hooks/useAuth';
import { setPreferredLanguage } from 'api/r2m';

// types
import { I18n } from 'types/config';

const SUPPORTED: { code: I18n; label: string; subtitle: string }[] = [
  { code: 'hu', label: 'Magyar', subtitle: '' },
  { code: 'en', label: 'English', subtitle: '' }
];

// ==============================|| LOCALIZATION ||============================== //

export default function LocalizationSection() {
  const {
    state: { borderRadius, i18n },
    setField
  } = useConfig();
  const { isLoggedIn } = useAuth();

  const theme = useTheme();
  const downMD = useMediaQuery(theme.breakpoints.down('md'));

  const [open, setOpen] = useState(false);
  const anchorRef = useRef<any>(null);

  const handleListItemClick = async (lng: I18n) => {
    setField('i18n', lng);
    setOpen(false);
    if (isLoggedIn) {
      try {
        await setPreferredLanguage(lng);
      } catch {
        // a UI már átállt, és localStorage-ben tárolva van — ha a backend nem
        // elérhető, a következő bejelentkezésnél a backend értéke felülírja.
      }
    }
  };

  const handleToggle = () => setOpen((prevOpen) => !prevOpen);

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }
    prevOpen.current = open;
  }, [open]);

  const currentLabel = i18n === 'hu' ? 'HU' : 'EN';

  return (
    <>
      <Box sx={{ ml: { xs: 0, sm: 2 } }}>
        <Tooltip title={i18n === 'hu' ? 'Nyelv' : 'Language'}>
          <Avatar
            variant="rounded"
            sx={{
              ...theme.typography.commonAvatar,
              ...theme.typography.mediumAvatar,
              transition: 'all .2s ease-in-out',
              color: theme.vars.palette.primary.dark,
              background: theme.vars.palette.primary.light,
              cursor: 'pointer',
              '&:hover, &[aria-controls="menu-list-grow"]': {
                color: theme.vars.palette.primary.light,
                background: theme.vars.palette.primary.main
              },
              ...theme.applyStyles('dark', {
                color: theme.vars.palette.primary.dark,
                background: theme.vars.palette.dark.main,
                '&:hover, &[aria-controls="menu-list-grow"]': {
                  color: theme.vars.palette.primary.light,
                  background: theme.vars.palette.primary.main
                }
              })
            }}
            ref={anchorRef}
            aria-controls={open ? 'menu-list-grow' : undefined}
            aria-haspopup="true"
            aria-label={i18n === 'hu' ? 'Nyelv váltása' : 'Change language'}
            onClick={handleToggle}
          >
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'inherit' }}>
              {currentLabel}
            </Typography>
          </Avatar>
        </Tooltip>
      </Box>

      <Popper
        placement={downMD ? 'bottom-start' : 'bottom-end'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        modifiers={[{ name: 'offset', options: { offset: [0, 12] } }]}
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={handleClose}>
            <Transitions position={downMD ? 'top-left' : 'top'} in={open} {...TransitionProps}>
              <Paper elevation={16}>
                {open && (
                  <List
                    sx={{
                      width: '100%',
                      minWidth: 180,
                      borderRadius: `${borderRadius}px`
                    }}
                  >
                    {SUPPORTED.map(({ code, label }) => (
                      <ListItemButton
                        key={code}
                        selected={i18n === code}
                        onClick={() => handleListItemClick(code)}
                      >
                        <ListItemText primary={<Typography>{label}</Typography>} />
                      </ListItemButton>
                    ))}
                  </List>
                )}
              </Paper>
            </Transitions>
          </ClickAwayListener>
        )}
      </Popper>
    </>
  );
}
